// Manager "Team" tab — the manager's support pathways, at `/manager/team`.
//
// THE FULL LIST LIVES HERE NOW (PO 2026-08-06). It used to be deliberately
// bare — "no search / filter / pager: a manager's own team is a small list,
// and `/manager` itself is the full case-management surface with all three".
// That rationale died when `/manager` became the four action cards at every
// width: this is the only pathway list left, so it inherited
// `ManagerCasesList` (search box, status filter, pager, URL-backed state) from
// the old desktop dashboard rather than leaving a manager with fifty pathways
// no way to find one. It is also the destination of the home page's "Current
// support pathways" card.
//
// IT IS ALSO THE PHONE'S ONLY ROUTE TO "New pathway", "Policies", "Settings"
// AND "Sign out". The bottom tab bar is the PO's mocked-up four (Home / Maeve
// / Team / Resources) with no "More" slot, and below `md` the desktop `Shell`
// — whose `MANAGER_NAV` and sidebar sign-out button are the only other links
// to those four — isn't rendered at all. Without the header action and the
// account block below, a phone user could not create a second pathway, browse
// the approved policy, reach their notification preferences, or sign out.
// **If a fifth "More" tab is ever approved (HR mobile already has one —
// `HrMore.tsx`), move these there; they live here because this is the one tab
// the PO didn't design.**
//
// Padding is the shell's job (`ManagerShell` pads the app-shell content area;
// the desktop `Shell` pads its `<main>`), so this page adds none of its own —
// otherwise it would double up at whichever width it's viewed at. It renders
// at every width: the tab bar only appears below `md`, but the path stays
// valid above it (a resized window, a shared link) rather than 404ing.

import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { CASE_STATUSES, type CasesListResponse } from '@besideu/shared/cases';
import { api } from '@/lib/api.ts';
import { useSession } from '@/lib/auth.ts';
import { useListSearchParams } from '@/lib/use-list-search-params.ts';
import { ROLE_LABELS, useSignOut } from '@/components/Shell.tsx';
import { Button } from '@/components/ui/Button.tsx';
import { NavCard } from '@/components/ui/Card.tsx';
import { Select } from '@/components/ui/Field.tsx';
import { StatusPill, ToneFor, humaniseStatus } from '@/components/ui/StatusPill.tsx';

const PAGE_SIZE = 50;

export function ManagerTeam() {
  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Your team</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Open a support pathway to draft your first response, prepare a touch point, or build a
            return-to-work plan with Maeve&rsquo;s help.
          </p>
        </div>
        <Link to="/manager/cases/new">
          <Button variant="primary" className="py-2 text-sm">
            + New support pathway
          </Button>
        </Link>
      </header>

      <ManagerCasesList />

      <AccountBlock />
    </div>
  );
}

// PROJECT.md Manager-portal gap #3 — search/filter/pagination for the pathway
// list, mirroring HrCases.tsx's URL-state pattern (self-scoped: no managerId
// filter, no CSV export). Moved here from `ManagerDashboard` when the home
// page became the four action cards (PO 2026-08-06); the `enabled` prop it
// used to take is gone with the viewport gate that needed it.
function ManagerCasesList() {
  const { searchParams, qParam, qInput, setQInput, page, setParam, setPage } =
    useListSearchParams();
  const status = searchParams.get('status') ?? '';

  const cases = useQuery({
    // UNDER THE `['cases']` PREFIX, not the `['manager','cases',…]` it carried
    // on the old desktop dashboard. Every Manager-side writer invalidates
    // `['cases']` (`ManagerNewCase`, `ManagerCaseDetail`'s status/reassign
    // mutations), and TanStack invalidation is prefix-matching — so a key
    // starting `['manager',…]` is never reached by any of them and this list
    // sat stale for the full `staleTime`. That is the exact divergence
    // `lib/use-cases.ts`'s header describes the mobile Home tab having had,
    // and it got worse in the move: the Team tab now owns the "+ New support
    // pathway" button (the phone's only route to it), so the most likely way
    // to reach this list was also the one guaranteed not to refresh it.
    // `'page'` keeps it from colliding with `['cases', id]` detail entries.
    queryKey: ['cases', 'page', { q: qParam, status, page }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (qParam) params.set('q', qParam);
      if (status) params.set('status', status);
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(page * PAGE_SIZE));
      return api.get<CasesListResponse>(`/api/cases?${params}`);
    },
    placeholderData: (prev) => prev,
  });

  const list = cases.data?.cases ?? [];
  const total = cases.data?.total ?? 0;
  const hasFilters = qParam !== '' || status !== '';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          placeholder="Search employee name"
          aria-label="Search employee name"
          className="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none sm:max-w-xs"
        />
        <Select
          aria-label="Filter by status"
          value={status}
          onChange={(e) => setParam('status', e.target.value)}
          className="w-auto py-2 text-sm"
        >
          <option value="">All statuses</option>
          {CASE_STATUSES.map((s) => (
            <option key={s} value={s}>
              {humaniseStatus(s)}
            </option>
          ))}
        </Select>
      </div>

      {cases.isPending ? (
        <p className="text-muted-foreground">Loading support pathways…</p>
      ) : cases.error ? (
        <p className="text-traffic-red">Could not load support pathways.</p>
      ) : total === 0 ? (
        <EmptyState hasFilters={hasFilters} />
      ) : (
        <>
          {list.length === 0 ? (
            // A valid `page` beyond the current result set (a stale/shared
            // URL, or the total shrinking after a support pathway was
            // actioned elsewhere) — `total > 0` here, so the pager below
            // still renders a live "Previous" the user can recover with.
            <p className="text-muted-foreground">No support pathways on this page.</p>
          ) : (
            <ul className="space-y-3">
              {list.map((c) => (
                <li key={c.id}>
                  <NavCard
                    to={`/manager/cases/${c.id}`}
                    title={c.employee.name}
                    titleTone="foreground"
                    subtitle={`${c.deceasedRelationship} · loss ${c.dateOfLoss}`}
                    meta={
                      <StatusPill tone={ToneFor(c.status)} dot>
                        {humaniseStatus(c.status)}
                      </StatusPill>
                    }
                  />
                </li>
              ))}
            </ul>
          )}

          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {`${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} of ${total}`}
            </span>
            <div className="flex gap-2">
              <Button variant="secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <Button
                variant="secondary"
                disabled={(page + 1) * PAGE_SIZE >= total}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function EmptyState({ hasFilters }: { hasFilters: boolean }) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/40 p-8 text-center">
      <p className="text-muted-foreground">
        {hasFilters
          ? 'No support pathways match the current search or filters.'
          : 'No support pathways yet.'}
      </p>
      {hasFilters ? null : (
        <Link to="/manager/cases/new">
          <Button variant="secondary" className="mt-3">
            Create the first one
          </Button>
        </Link>
      )}
    </div>
  );
}

// Mirrors the desktop sidebar's footer (`AppNav.tsx`'s `SidebarFrame`): who
// you're signed in as, settings, sign out. It exists because the mobile
// bottom tab bar has no "More" slot — below `lg` this is the phone/tablet's
// only route to Policies/Settings/Sign out. `lg:hidden` above that — NOT
// `md:hidden`, even though `ManagerShell` passes `breakpoint="md"` to `Shell`:
// that prop only swaps the tab bar for `MobileTopNav` (a slim bar with a
// generic eyebrow + Sign out, no name/role), and `AppSidebar` itself is
// hardcoded `lg:flex` in `AppNav.tsx` regardless of that prop. Hiding at `md`
// left a 768–1023px gap where neither this block nor the sidebar showed the
// signed-in user's name/role.
function AccountBlock() {
  const session = useSession('manager-shell');
  const logout = useSignOut();
  const name = 'name' in session ? session.name : '';

  return (
    <section className="border-t border-border pt-4 lg:hidden">
      <h2 className="sr-only">Your account</h2>
      <p className="text-sm font-medium text-foreground">
        {name || ROLE_LABELS[session.role] || 'Signed in'}
      </p>
      <p className="text-xs text-muted-foreground">{ROLE_LABELS[session.role]}</p>
      <div className="mt-3 flex flex-wrap items-center gap-4">
        <Link to="/manager/policies" className="text-sm font-medium text-primary">
          Policies
        </Link>
        <Link to="/manager/settings" className="text-sm font-medium text-primary">
          Settings
        </Link>
        <button
          type="button"
          onClick={() => logout.mutate()}
          disabled={logout.isPending}
          className="text-sm font-medium text-muted-foreground transition hover:text-foreground disabled:opacity-60"
        >
          {logout.isPending ? 'Signing out…' : 'Sign out'}
        </button>
      </div>
    </section>
  );
}
